import { useState } from 'react';
import api from './api';

const RED = 'var(--accent)';

// ─────────────────────────────────────────────────────────────────────────────
// ConsentSignPage — електронний підпис згоди на обробку персональних даних.
// Показується після GuidePage, до онбординг-візарда. Повністю електронно:
// чекбокс "Я прочитав і погоджуюсь" + введене повне ім'я — без друку/сканування.
// Бекенд штампує timestamp+IP на готовий PDF і зберігає як документ фрілансера
// (category='consent'), звідки він автоматично зʼявляється і в адміна,
// і в My Documents фрілансера.
// ─────────────────────────────────────────────────────────────────────────────
export default function ConsentSignPage({ freelancer, onDone }) {
  const defaultName = [freelancer?.firstName, freelancer?.lastName].filter(Boolean).join(' ');
  const [fullName, setFullName] = useState(defaultName);
  const [agree,    setAgree]    = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const canSign = agree && fullName.trim().length > 1;

  const handleSign = async () => {
    if (!canSign) return;
    setSaving(true);
    setError('');
    try {
      const res = await api.freelancers.signConsent({ fullName: fullName.trim(), agree: true });
      onDone(res.consentSignedAt);
    } catch (e) {
      setError(e.message || 'Failed to sign. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: '560px' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ color: RED, fontWeight: 'bold', fontSize: '12px', letterSpacing: '1px', marginBottom: '8px' }}>
            SCULL CREW — CREW PORTAL
          </div>
          <h1 style={{ color: 'var(--text-primary)', fontSize: '22px', margin: '0 0 8px' }}>Consent to the Processing and Storage of Personal Data</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
            One last step before you fill in your profile — please review and sign electronically.
          </p>
        </div>

        <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px' }}>
          <p style={{ color: 'var(--text-body)', fontSize: '13px', lineHeight: 1.6, margin: '0 0 6px' }}>
            This document explains what personal data Scull Crew collects about you as a freelance crew member,
            why, and what rights you have over it (access, rectification, erasure, and more).
          </p>
          <a href="/docs/Freelancer_Data_Processing_Consent.pdf" target="_blank" rel="noreferrer"
            style={{ color: RED, fontSize: '12px', display: 'inline-block', margin: '4px 0 20px' }}>
            📄 Read the full document
          </a>

          {error && (
            <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '18px' }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
              style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0, accentColor: RED }} />
            <span style={{ color: 'var(--text-body)', fontSize: '13px', lineHeight: 1.5 }}>
              I have read and understood this notice. I freely give my consent to Scull Crew (PROF MEDIA - FZCO)
              processing and storing my personal data and documents for the purposes described. I understand I
              may withdraw this consent at any time.
            </span>
          </label>

          <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
            Type your full name as your electronic signature
          </label>
          <input
            value={fullName} onChange={e => setFullName(e.target.value)}
            placeholder="Ivan Kovalenko"
            style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', padding: '11px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '22px' }}
          />

          <button
            type="button" onClick={handleSign} disabled={!canSign || saving}
            style={{
              width: '100%', background: (!canSign || saving) ? 'var(--text-dim)' : RED, color: 'var(--text-primary)', border: 'none',
              borderRadius: '8px', padding: '14px', fontSize: '14px', fontWeight: 700,
              cursor: (!canSign || saving) ? 'not-allowed' : 'pointer',
            }}>
            {saving ? 'Signing…' : 'Sign electronically'}
          </button>
          <p style={{ color: 'var(--text-dim)', fontSize: '11px', textAlign: 'center', marginTop: '10px', marginBottom: 0 }}>
            Your name, timestamp, and IP address will be recorded as your electronic signature.
          </p>
        </div>
      </div>
    </div>
  );
}
